import { usePersistedState } from "@/lib/persistedFilters";
// Commissions — Phase 1 UI. Uploads Stax Bill's monthly Commission Report
// CSV, catalogues rows in Mongo, and lets staff browse by period + drill
// down per agent. Phase 2 (backlog) swaps the CSV import for a compute
// engine that pulls customers/invoices from Stax and applies term-based
// rates from the product catalogue.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  DollarSign, Upload, Loader2, Search, Trash2, Download, X, Save,
  Users, BadgeDollarSign, Package, Sparkles, Info, Link2, Link2Off,
  TrendingUp, TrendingDown, Columns3, ArrowUp, ArrowDown, FileText, Cog, Receipt, AlertTriangle, CalendarClock,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuCheckboxItem, DropdownMenuSeparator, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { useTablePagination, TablePagination, PageSizeSelect } from "@/components/TablePagination";
import { CA_TAX_PRESETS } from "@/pages/Partners";
import { CommissionAliasFixDialog } from "@/components/CommissionAliasFixDialog";
import { MyDealsCard } from "@/components/commissions/MyDealsCard";
import { NewDealDigestDialog } from "@/components/commissions/NewDealDigestDialog";
import { WorkflowConfigDialog } from "@/components/commissions/WorkflowConfigDialog";
import { ProformaActionsCard } from "@/components/commissions/ProformaActionsCard";
import { OpenInquiriesButton } from "@/components/commissions/OpenInquiriesButton";

const LEVELS = ["Bronze", "Silver", "Gold", "Platinum", "No Commission"];
const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// -- Data cleaners ------------------------------------------------------
// Stax exports partner names with a leading `..` (their internal marker
// for reseller / territory codes). Strip it for display.
// (definition moved below `decodeHtmlEntities`)

// Stax exports often contain HTML-encoded characters (e.g. `Chant&#039;s`
// instead of `Chant's`). Decode common entities before display since React
// otherwise renders them literally as escaped text.
const decodeHtmlEntities = (s) => (s || "")
  .replace(/&#(\d+);/g,  (_, n) => String.fromCharCode(parseInt(n, 10)))
  .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCharCode(parseInt(h, 16)))
  .replace(/&amp;/g,  "&")
  .replace(/&quot;/g, "\"")
  .replace(/&apos;/g, "'")
  .replace(/&lt;/g,   "<")
  .replace(/&gt;/g,   ">");

// Agent names in the Stax export sometimes get suffixed with the PSM tag,
// e.g. "Agent Myra Systems Corp. (Dino x226)" — occasionally with malformed
// closing parens like "…(Dino x226))". Strip everything from the last "("
// onward, plus the leading "Agent " prefix.
const cleanAgent = (s) => decodeHtmlEntities((s || "")
  .replace(/\s*\([^(]*$/, "")
  .replace(/^\s*agent\s+/i, "")
  .trim());
// Partner display: strip Stax `..` prefix + decode HTML entities.
const cleanPartner = (s) => decodeHtmlEntities((s || "").replace(/^\.\.+/, ""));
// PSM field arrives as "(Dino x226)" — pull the name before the extension.
// Also tolerates "Dino x226" (no parens) and plain "Dino".
const cleanPsm = (s) => {
  const raw = decodeHtmlEntities((s || "").trim());
  if (!raw) return "";
  const m = raw.match(/\(?\s*([A-Za-z][A-Za-z' -]*?)\s+x\d+\s*\)?/);
  if (m) return m[1].trim();
  return raw.replace(/[()]/g, "").trim();
};

// -- Column registry (mirrors DidMaster / Hardware pattern). Order &
// visibility persisted per-user in localStorage.
const COLUMNS = [
  // Active/Paused status indicator. Sortable so staff can group paused
  // partners together at the top of the list. `paused` is decorated on each
  // row by the backend from the partner org's pause state.
  { key: "status",          label: "Status", sortable: true, defaultHidden: false, align: "center",
    text: (r) => r.paused ? "1-paused" : "0-active",
    render: (r) => (
      <span className="inline-flex items-center gap-1.5" title={r.paused ? "Paused" : "Active"}
            data-testid={`commissions-status-${r.row_id}`}>
        <span className={`h-2.5 w-2.5 rounded-full ring-2 ring-white
                          ${r.paused
                            ? "bg-rose-500 shadow-[0_0_0_2px_rgba(244,63,94,0.25)]"
                            : "bg-emerald-500 shadow-[0_0_0_2px_rgba(16,185,129,0.25)]"}`} />
      </span>
    ) },
  { key: "agent",           label: "Agent", sortable: true, defaultHidden: false,
    text: (r) => cleanAgent(r.agent),
    render: (r, ctx) => {
      const partner = cleanPartner(r.territory);
      const detailKey = cleanAgent(r.agent) || partner;
      return (
        <div className="flex flex-col">
          <Link to={`/commissions/partners/${encodeURIComponent(detailKey)}`}
                data-testid={`commissions-agent-${r.row_id}`}
                onClick={() => ctx?.setDrilldown && ctx.setDrilldown(null)}
                className="font-medium text-slate-800 hover:text-primary hover:underline text-left">
            {cleanAgent(r.agent)}
          </Link>
          {r.territory && (
            <Link to={`/partners?q=${encodeURIComponent(partner)}&types=${encodeURIComponent("Agent,Master Agent")}`}
                  data-testid={`commissions-partner-link-${r.row_id}`}
                  className="text-[11px] text-slate-400 hover:text-primary hover:underline"
                  title={`Open ${partner} in Partners`}>
              {r.territory}
            </Link>
          )}
        </div>
      );
    } },
  { key: "psm_name",        label: "PSM", sortable: true, defaultHidden: false,
    text: (r) => cleanPsm(r.psm_name),
    render: (r) => {
      const n = cleanPsm(r.psm_name);
      return n ? <span className="text-xs text-slate-600">{n}</span> : <span className="text-slate-300">—</span>;
    } },
  { key: "level_name",      label: "Level", sortable: true, defaultHidden: false,
    text: (r) => r.level_name || "",
    render: (r) => <LevelPill level={r.level_name} /> },
  { key: "deals",           label: "Deals", sortable: true, defaultHidden: false, align: "right",
    text: (r) => String(r.deals ?? 0),
    render: (r) => <span className="tabular-nums">{r.deals}</span> },
  { key: "total",           label: "Revenue", sortable: true, defaultHidden: false, align: "right",
    text: (r) => String(r.total ?? 0),
    render: (r) => <span className="tabular-nums text-slate-700">{money(r.total)}</span> },
  { key: "commission",      label: "Commission", sortable: true, defaultHidden: false, align: "right",
    text: (r) => String(r.commission ?? 0),
    render: (r) => <span className="tabular-nums font-medium text-emerald-700">{money(r.commission)}</span> },
  { key: "rated_commission", label: "Recurring", sortable: true, defaultHidden: false, align: "right",
    text: (r) => String(r.rated_commission ?? ""),
    render: (r) => <SplitMoney value={r.rated_commission} testid={`commissions-recurring-${r.row_id}`} /> },
  { key: "onetime_commission", label: "One-time & HW", sortable: true, defaultHidden: false, align: "right",
    text: (r) => String(r.onetime_commission ?? ""),
    render: (r) => <SplitMoney value={r.onetime_commission} muted testid={`commissions-onetime-${r.row_id}`} /> },
  { key: "standard_commission_rate", label: "Avg Rate", sortable: true, defaultHidden: false, align: "right",
    text: (r) => String(r.standard_commission_rate ?? 0),
    render: (r, ctx) => <AvgRateCell rate={r.standard_commission_rate} row={r} isStaff={ctx?.isStaff}
                                     testid={`commissions-rate-${r.row_id}`} /> },
  { key: "trend",           label: "Trend", sortable: false, defaultHidden: false, align: "center",
    text: () => "",
    render: (r, ctx) => <Sparkline points={ctx?.historyMap?.[r.agent] || []} /> },
  { key: "recurring_trend", label: "Recurring trend", sortable: false, defaultHidden: false, align: "center",
    text: () => "",
    render: (r, ctx) => <Sparkline points={ctx?.historyMap?.[r.agent] || []} field="recurring_revenue"
                                   label="Recurring revenue" testid="sparkline-recurring" /> },
];
const COL_MAP  = Object.fromEntries(COLUMNS.map((c) => [c.key, c]));
const ALL_KEYS = COLUMNS.map((c) => c.key);
const DEFAULT_HIDDEN = COLUMNS.filter((c) => c.defaultHidden).map((c) => c.key);

export default function Commissions() {
  const { user, has } = useAuth();
  const navigate = useNavigate();
  const isStaff = ((user?.base_role || user?.role || "").toLowerCase() === "staff") ||
                  ((user?.role || "").toLowerCase() === "super_admin");
  const isSuperAdmin = (user?.role || "").toLowerCase() === "super_admin";
  const [searchParams] = useSearchParams();
  const PREF_KEY = `commissions_cols_v1_${user?.user_id || "anon"}`;
  const [periods, setPeriods] = useState([]);
  const [activePeriod, setActivePeriod] = useState("");   // period_id
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [q, setQ] = usePersistedState("commissions.q", "", searchParams.get("q"));
  const [levelFilter, setLevelFilter] = usePersistedState("commissions.level", "all");
  const [psmFilter, setPsmFilter] = usePersistedState("commissions.psm", "all");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [aliasDialog,      setAliasDialog]      = useState(null); // { preview, applying, result }
  const [remindersDialog,  setRemindersDialog]  = useState(null); // { config, ... }
  const [digestOpen,       setDigestOpen]       = useState(false);
  const [workflowOpen,     setWorkflowOpen]     = useState(false);
  const [accountingAccess, setAccountingAccess] = useState(false);
  const [agentLinksDialog, setAgentLinksDialog] = useState(null); // { links, users, unlinked }
  const [missingTaxDialog, setMissingTaxDialog] = useState(null); // { rows, saving_org_id }
  const [missingEmailDialog, setMissingEmailDialog] = useState(null); // { rows, savingOrgId }
  const [sandboxLogDialog, setSandboxLogDialog] = useState(null); // { items }
  const [deleteDialog, setDeleteDialog] = useState(null); // { period_id, period_label }
  const [deleteTyped, setDeleteTyped] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [sandboxOn, setSandboxOn] = useState(false);
  // 0 = not in tour, 1 = missing tax step, 2 = missing email, 3 = alias backfill
  const [tourStep, setTourStep] = useState(0);
  // Range report mode: "single" = existing single-period view; presets or "custom"
  // switch to `/commissions/report` and show an aggregated table.
  const RANGE_PREF_KEY = `commissions_range_v1_${user?.user_id || "anon"}`;
  const [rangeKey, setRangeKey] = useState(() => {
    try {
      const p = JSON.parse(localStorage.getItem(RANGE_PREF_KEY) || "{}");
      return p.rangeKey || "single";
    } catch { return "single"; }
  });
  const [customFrom, setCustomFrom] = useState(() => {
    try { return JSON.parse(localStorage.getItem(RANGE_PREF_KEY) || "{}").customFrom || ""; } catch { return ""; }
  });
  const [customTo,   setCustomTo]   = useState(() => {
    try { return JSON.parse(localStorage.getItem(RANGE_PREF_KEY) || "{}").customTo || ""; } catch { return ""; }
  });
  useEffect(() => {
    localStorage.setItem(RANGE_PREF_KEY, JSON.stringify({ rangeKey, customFrom, customTo }));
  }, [RANGE_PREF_KEY, rangeKey, customFrom, customTo]);
  const [rangeData,  setRangeData]  = useState(null);
  const [rangeLoad,  setRangeLoad]  = useState(false);

  // Compute {frm,to} in `YYYY-MM` for a preset. `null` = "single period".
  const computeRange = (key) => {
    if (key === "single") return null;
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();  // 0-11
    const fmt = (yy, mm) => `${yy}-${String(mm + 1).padStart(2, "0")}`;
    if (key === "custom") {
      return { frm: customFrom, to: customTo };
    }
    if (key === "this_month") return { frm: fmt(y, m),     to: fmt(y, m) };
    if (key === "last_month") {
      const d = new Date(y, m - 1, 1);
      const s = fmt(d.getFullYear(), d.getMonth());
      return { frm: s, to: s };
    }
    if (key === "this_quarter") {
      const q = Math.floor(m / 3) * 3;
      return { frm: fmt(y, q), to: fmt(y, m) };
    }
    if (key === "last_quarter") {
      const q = Math.floor(m / 3) * 3;
      const start = new Date(y, q - 3, 1);
      const end   = new Date(y, q - 1, 1);
      return { frm: fmt(start.getFullYear(), start.getMonth()),
               to:  fmt(end.getFullYear(),   end.getMonth()) };
    }
    if (key === "ytd") return { frm: fmt(y, 0), to: fmt(y, m) };
    return null;
  };
  const rangeSpec = computeRange(rangeKey);
  useEffect(() => {
    if (!rangeSpec || !rangeSpec.frm || !rangeSpec.to) { setRangeData(null); return; }
    setRangeLoad(true);
    api.get(`/commissions/report`, { params: { frm: rangeSpec.frm, to: rangeSpec.to } })
      .then((r) => setRangeData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Report failed"))
      .finally(() => setRangeLoad(false));
  }, [rangeKey, customFrom, customTo]);

  const exportRange = (kind) => {
    if (!rangeSpec?.frm || !rangeSpec?.to) return;
    const params = new URLSearchParams({ frm: rangeSpec.frm, to: rangeSpec.to });
    const suffix = kind === "pdf" ? "report.pdf" : "report.csv";
    window.open(`${api.defaults.baseURL}/commissions/${suffix}?${params.toString()}`, "_blank");
  };
  const [drilldown, setDrilldown] = useState(null);       // agent name
  // Sort & column-picker state (persisted per user).
  const [sort, setSort] = useState({ key: "commission", dir: "desc" });
  const [order, setOrder] = useState(ALL_KEYS);
  const [hidden, setHidden] = useState(DEFAULT_HIDDEN);
  useEffect(() => {
    try {
      const p = JSON.parse(localStorage.getItem(PREF_KEY) || "{}");
      if (Array.isArray(p.order)) {
        const ord = p.order.filter((k) => COL_MAP[k]);
        // Columns added since the order was saved slot in after their default neighbour.
        ALL_KEYS.forEach((k, i) => {
          if (ord.includes(k)) return;
          const at = i === 0 ? 0 : ord.indexOf(ALL_KEYS[i - 1]) + 1;
          ord.splice(at > 0 ? at : ord.length, 0, k);
        });
        setOrder(ord);
      }
      if (Array.isArray(p.hidden)) setHidden(p.hidden.filter((k) => COL_MAP[k]));
      if (p.sort && COL_MAP[p.sort.key]) setSort(p.sort);
    } catch { /* keep defaults */ }
  }, [PREF_KEY]);
  const persist = (patch) => {
    const cur = { order, hidden, sort, ...patch };
    localStorage.setItem(PREF_KEY, JSON.stringify(cur));
  };
  const toggleHidden = (k) => setHidden((h) => {
    const nh = h.includes(k) ? h.filter((x) => x !== k) : [...h, k];
    persist({ hidden: nh }); return nh;
  });
  const resetCols = () => { setOrder(ALL_KEYS); setHidden(DEFAULT_HIDDEN); persist({ order: ALL_KEYS, hidden: DEFAULT_HIDDEN }); };
  const toggleSort = (key) => {
    setSort((s) => {
      const next = { key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" };
      persist({ sort: next }); return next;
    });
  };
  const visibleCols = useMemo(
    () => order.filter((k) => !hidden.includes(k)).map((k) => COL_MAP[k]).filter(Boolean),
    [order, hidden]);

  const activePeriodDoc = periods.find((p) => p.period_id === activePeriod);

  const loadPeriods = async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: s }] = await Promise.all([
        api.get("/commissions/periods"),
        api.get("/commissions/stats"),
      ]);
      const items = p.items || [];
      setPeriods(items); setStats(s);
      // Default to newest period on first load.
      setActivePeriod((cur) => cur || items[0]?.period_id || "");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load periods");
    } finally { setLoading(false); }
  };
  useEffect(() => { loadPeriods(); }, []);

  const loadRows = async () => {
    if (!activePeriod) { setRows([]); return; }
    setRowsLoading(true);
    try {
      const { data } = await api.get(`/commissions/summary?period_id=${activePeriod}`);
      setRows(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load rows");
    } finally { setRowsLoading(false); }
  };
  useEffect(() => { loadRows(); }, [activePeriod]);

  // Per-agent sparkline data (all periods, oldest → newest). Loaded once.
  const [historyMap, setHistoryMap] = useState({});
  useEffect(() => {
    api.get("/commissions/history").then((r) => setHistoryMap(r.data?.items || {}))
      .catch(() => setHistoryMap({}));
  }, []);

  const filtered = useMemo(() => {
    let out = rows;
    if (q.trim()) {
      // Multi-column substring search using each column's `text()` extractor
      // — search matches whatever the user sees on screen (with `..` stripped).
      const s = q.trim().toLowerCase();
      out = out.filter((r) => COLUMNS.some((c) => (c.text(r) || "").toLowerCase().includes(s)));
    }
    if (levelFilter !== "all") {
      const want = levelFilter.toLowerCase();
      // "No Commission" also catches unmapped rows with no level yet.
      out = out.filter((r) => {
        const lv = (r.level_name || "").toLowerCase();
        return lv === want || (want === "no commission" && !lv);
      });
    }
    if (psmFilter !== "all") {
      out = out.filter((r) => cleanPsm(r.psm_name).toLowerCase() === psmFilter.toLowerCase());
    }
    // Sort by the active column's `text()` (numeric-aware). Numeric columns
    // (deals/total/commission/rate/sequence) sort naturally as numbers via
    // `localeCompare(..., {numeric: true})`.
    out = [...out].sort((a, b) => {
      const col = COL_MAP[sort.key] || COL_MAP.agent;
      const cmp = String(col.text(a)).localeCompare(String(col.text(b)),
                                                    undefined, { numeric: true });
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return out;
  }, [rows, q, levelFilter, psmFilter, sort]);

  // Distinct PSM names (cleaned) for the filter dropdown.
  const psmOptions = useMemo(() => {
    const s = new Set();
    for (const r of rows) {
      const n = cleanPsm(r.psm_name);
      if (n) s.add(n);
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const pg = useTablePagination(filtered, 50);

  // Metrics reflect the CURRENT view (period + search + level filter), not
  // all-time totals. Recomputed whenever `filtered` changes.
  const viewStats = useMemo(() => {
    const uniqAgents = new Set();
    let deals = 0, revenue = 0, commission = 0;
    for (const r of filtered) {
      if (r.agent) uniqAgents.add(r.agent);
      deals      += Number(r.deals || 0);
      revenue    += Number(r.total || 0);
      commission += Number(r.commission || 0);
    }
    return {
      agents:     uniqAgents.size,
      deals,
      revenue:    Math.round(revenue * 100) / 100,
      commission: Math.round(commission * 100) / 100,
    };
  }, [filtered]);

  const openDeletePeriod = () => {
    if (!activePeriodDoc) return;
    setDeleteTyped("");
    setDeleteDialog({ period_id: activePeriod, period_label: activePeriodDoc.period_label });
  };
  const submitDeletePeriod = async () => {
    if (!deleteDialog) return;
    setDeleteBusy(true);
    try {
      const { data } = await api.post(
        `/commissions/periods/${deleteDialog.period_id}/request-deletion`,
        { confirm_period_label: deleteTyped.trim() });
      toast.success(`Deletion request sent for approval to ${data.approver}`);
      setDeleteDialog(null);
      setDeleteTyped("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send deletion request");
    } finally {
      setDeleteBusy(false);
    }
  };

  const exportPeriod = () => {
    if (!activePeriod) return;
    window.open(
      `${process.env.REACT_APP_BACKEND_URL}/api/commissions/export.csv?period_id=${activePeriod}`,
      "_blank"
    );
  };

  // -- Alias backfill + Reminders admin dialogs -------------------------
  const openAliasBackfill = async () => {
    setAliasDialog({ loading: true });
    try {
      const { data: preview } = await api.post("/commissions/aliases/backfill?dry_run=true", {});
      setAliasDialog({ preview, applying: false, result: null });
    } catch (e) {
      setAliasDialog(null);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to preview aliases");
    }
  };
  const applyAliasBackfill = async () => {
    setAliasDialog((s) => ({ ...(s || {}), applying: true }));
    try {
      const { data: result } = await api.post("/commissions/aliases/backfill", {});
      toast.success(`Matched ${result.matched?.length || 0} · Conflicts ${result.conflicts?.length || 0}`);
      setAliasDialog((s) => ({ ...(s || {}), result, applying: false }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to apply backfill");
      setAliasDialog((s) => ({ ...(s || {}), applying: false }));
    }
  };

  const [aliasFix, setAliasFix] = useState(false);

  const openReminders = async () => {
    try {
      const { data: cfg } = await api.get("/commissions/reminders/config");
      setRemindersDialog({ cfg, saving: false, dryRun: null, sending: false });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load reminder settings");
    }
  };
  const saveReminders = async (patch) => {
    setRemindersDialog((s) => ({ ...(s || {}), saving: true }));
    try {
      const { data: cfg } = await api.put("/commissions/reminders/config", patch);
      toast.success("Saved");
      setSandboxOn(!!cfg.sandbox);
      setRemindersDialog((s) => ({ ...(s || {}), cfg, saving: false }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
      setRemindersDialog((s) => ({ ...(s || {}), saving: false }));
    }
  };
  const previewReminders = async () => {
    setRemindersDialog((s) => ({ ...(s || {}), sending: true }));
    try {
      const { data: r } = await api.post("/commissions/reminders/send-now?dry_run=true", {});
      setRemindersDialog((s) => ({ ...(s || {}), dryRun: r, sending: false, unticked: s?.unticked || [] }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
      setRemindersDialog((s) => ({ ...(s || {}), sending: false }));
    }
  };
  const sendReminders = async () => {
    const preview = remindersDialog?.dryRun?.dry_run ? remindersDialog.dryRun : null;
    const unticked = remindersDialog?.unticked || [];
    const keys = preview ? preview.eligible.map((p) => p.partner_key).filter((k) => !unticked.includes(k)) : null;
    if (keys && keys.length === 0) { toast.error("Tick at least one partner to send to"); return; }
    if (!window.confirm(keys
      ? `Send reminder emails now to the ${keys.length} ticked partner${keys.length === 1 ? "" : "s"}?`
      : "Send reminder emails now to every eligible partner with a contact email?")) return;
    setRemindersDialog((s) => ({ ...(s || {}), sending: true }));
    try {
      const { data: r } = await api.post("/commissions/reminders/send-now", keys ? { partner_keys: keys } : {});
      toast.success(`Sent ${r.sent?.length || 0} · Failed ${r.failed?.length || 0}`);
      setRemindersDialog((s) => ({ ...(s || {}), dryRun: r, sending: false, unticked: [] }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
      setRemindersDialog((s) => ({ ...(s || {}), sending: false }));
    }
  };

  const openAgentLinks = async () => {
    setAgentLinksDialog({ loading: true });
    try {
      const [{ data: linksData }, { data: unlinkedData }, { data: usersData }] =
        await Promise.all([
          api.get("/commissions/agent-links"),
          api.get("/commissions/agent-links/unlinked-agents"),
          api.get("/users"),
        ]);
      const rawUsers = Array.isArray(usersData) ? usersData : (usersData.items || []);
      setAgentLinksDialog({
        links:    linksData.items || [],
        unlinked: unlinkedData.unlinked || [],
        users:    rawUsers
          .filter((u) => (u.base_role || "staff") === "staff" && u.status === "active")
          .map((u) => ({
            user_id: u.user_id, name: u.name || u.email, email: u.email,
          }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
        query: "",
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load agent links");
      setAgentLinksDialog(null);
    }
  };
  const saveAgentLink = async (agent, userId) => {
    try {
      await api.put(`/commissions/agent-links/${encodeURIComponent(agent)}`, { user_id: userId || "" });
      toast.success(userId ? `Linked ${agent}` : `Unlinked ${agent}`);
      // Refresh the dialog data.
      const [{ data: linksData }, { data: unlinkedData }] = await Promise.all([
        api.get("/commissions/agent-links"),
        api.get("/commissions/agent-links/unlinked-agents"),
      ]);
      setAgentLinksDialog((s) => ({ ...(s || {}),
        links: linksData.items || [], unlinked: unlinkedData.unlinked || [] }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update link");
    }
  };

  const openMissingTax = async () => {
    setMissingTaxDialog({ loading: true });
    try {
      const { data } = await api.get("/commissions/partners/missing-tax");
      setMissingTaxDialog({
        rows: (data.missing || []).map((r) => ({ ...r,
          _tax_rate: r.tax_rate ?? "",
          _tax_jurisdiction: r.tax_jurisdiction || "",
          _tax_number: r.tax_number || "" })),
        complete_count: data.complete_count || 0,
        total: data.total || 0,
        savingOrgId: "",
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load partners");
      setMissingTaxDialog(null);
    }
  };
  const saveMissingTax = async (row) => {
    if (!row._tax_rate && !row._tax_jurisdiction && !row._tax_number) {
      toast.error("Enter at least a tax rate or jurisdiction first");
      return;
    }
    setMissingTaxDialog((s) => ({ ...(s || {}), savingOrgId: row.org_id }));
    try {
      const patch = {};
      if (row._tax_rate !== "") patch.tax_rate = Number(row._tax_rate);
      if (row._tax_jurisdiction) patch.tax_jurisdiction = row._tax_jurisdiction;
      if (row._tax_number)       patch.tax_number       = row._tax_number;
      await api.patch(`/commissions/partners/orgs/${row.org_id}/tax`, patch);
      toast.success(`Saved ${row.name}`);
      // Remove from the list on success.
      setMissingTaxDialog((s) => ({
        ...(s || {}),
        rows: (s?.rows || []).filter((r) => r.org_id !== row.org_id),
        complete_count: (s?.complete_count || 0) + 1,
        savingOrgId: "",
      }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
      setMissingTaxDialog((s) => ({ ...(s || {}), savingOrgId: "" }));
    }
  };
  const updateMissingRow = (org_id, patch) => {
    setMissingTaxDialog((s) => ({ ...(s || {}),
      rows: (s?.rows || []).map((r) => r.org_id === org_id ? { ...r, ...patch } : r) }));
  };

  // --- Missing contact email bulk editor (same shape as tax dialog) ----
  const openMissingEmail = async () => {
    setMissingEmailDialog({ loading: true });
    try {
      const { data } = await api.get("/commissions/partners/missing-email");
      setMissingEmailDialog({
        rows: (data.missing || []).map((r) => ({ ...r,
          _contact_email: r.contact_email || "",
          _contact_name:  r.contact_name  || "",
          _contact_phone: r.contact_phone || "" })),
        complete_count: data.complete_count || 0,
        total: data.total || 0,
        savingOrgId: "",
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load partners");
      setMissingEmailDialog(null);
    }
  };
  const saveMissingEmail = async (row) => {
    if (!row._contact_email) {
      toast.error("Enter a contact email");
      return;
    }
    setMissingEmailDialog((s) => ({ ...(s || {}), savingOrgId: row.org_id }));
    try {
      const patch = { contact_email: row._contact_email };
      if (row._contact_name)  patch.contact_name  = row._contact_name;
      if (row._contact_phone) patch.contact_phone = row._contact_phone;
      await api.patch(`/commissions/partners/orgs/${row.org_id}/contact`, patch);
      toast.success(`Saved ${row.name}`);
      setMissingEmailDialog((s) => ({
        ...(s || {}),
        rows: (s?.rows || []).filter((r) => r.org_id !== row.org_id),
        complete_count: (s?.complete_count || 0) + 1,
        savingOrgId: "",
      }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
      setMissingEmailDialog((s) => ({ ...(s || {}), savingOrgId: "" }));
    }
  };
  const updateMissingEmailRow = (org_id, patch) => {
    setMissingEmailDialog((s) => ({ ...(s || {}),
      rows: (s?.rows || []).map((r) => r.org_id === org_id ? { ...r, ...patch } : r) }));
  };

  // --- Sandbox log viewer ---------------------------------------------
  const loadSandboxLog = async (filters = {}) => {
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (filters.q)         params.set("q",         filters.q);
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to)   params.set("date_to",   filters.date_to);
      const { data } = await api.get(`/commissions/reminders/sandbox-log?${params}`);
      setSandboxLogDialog((s) => ({ ...(s || {}),
        items: data.items || [],
        filtered: !!data.filtered,
        filters,
        loading: false }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load log");
      setSandboxLogDialog(null);
    }
  };
  const openSandboxLog = async () => {
    setSandboxLogDialog({ loading: true, filters: { q: "", date_from: "", date_to: "" } });
    await loadSandboxLog({});
  };

  // --- Sandbox banner status ------------------------------------------
  useEffect(() => {
    // Only staff can read the reminder config. Silently ignore permission
    // failures for non-admin viewers.
    if (!isStaff) return;
    (async () => {
      try {
        const { data } = await api.get("/commissions/reminders/config");
        setSandboxOn(!!data.sandbox);
      } catch { /* not a super admin — leave banner hidden */ }
      try {
        const { data } = await api.get("/commissions/workflow/config");
        setAccountingAccess(!!data.i_am_accounting);
      } catch { /* staff without access — button stays hidden */ }
    })();
  }, [isStaff]);

  // --- Auto-open the admin dialog from `?admin=missing-tax|missing-email
  // |alias-backfill|tour` (deep-link target for the dashboard readiness tile).
  useEffect(() => {
    if (!isSuperAdmin) return;
    const which = searchParams.get("admin");
    if (!which) return;
    if      (which === "missing-tax")     { setTourStep((s) => s || 0); openMissingTax(); }
    else if (which === "missing-email")   { setTourStep((s) => s || 0); openMissingEmail(); }
    else if (which === "alias-backfill")  { setTourStep((s) => s || 0); openAliasBackfill(); }
    else if (which === "reminders")       openReminders();
    else if (which === "digest")          setDigestOpen(true);
    else if (which === "workflow")        setWorkflowOpen(true);
    else if (which === "agent-links")     openAgentLinks();
    else if (which === "sandbox-log")     openSandboxLog();
    else if (which === "tour")            { setTourStep(1); openMissingTax(); }
  }, [isSuperAdmin, searchParams]);

  // Period-level totals from the doc (avoids re-summing on every render).
  const totals = activePeriodDoc || {};

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6" data-testid="commissions-page">
      <PageHeader testid="commissions-header" title="Commissions" icon={DollarSign}
        subtitle={isStaff
          ? "Monthly commission tracking for partners and agents"
          : "Your monthly commissions"}>
        {isStaff && (
          <Button variant="outline" onClick={() => navigate("/commissions/engine")}
                  data-testid="commissions-engine-btn" className="h-9">
            <Cog className="h-4 w-4" /> Engine
          </Button>
        )}
        {isStaff && (
          <Button variant="outline" onClick={() => navigate("/commissions/payments")}
                  data-testid="commissions-payment-history-btn" className="h-9">
            <Receipt className="h-4 w-4" /> Payment history
          </Button>
        )}
        {(isSuperAdmin || accountingAccess) && (
          <Button variant="outline" onClick={() => navigate("/commissions/accounting")}
                  data-testid="commissions-accounting-btn" className="h-9">
            <Receipt className="h-4 w-4" /> Accounting queue
          </Button>
        )}
        {isStaff && <OpenInquiriesButton />}
        {isStaff && activePeriod && (
          <Button variant="outline" onClick={exportPeriod} data-testid="commissions-export">
            <Download className="h-4 w-4" /> Export period
          </Button>
        )}
        {isSuperAdmin && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="h-9" data-testid="commissions-admin-menu">
                <Sparkles className="h-4 w-4" /> Admin
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Commissions admin</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setAliasFix(true)}
                                data-testid="commissions-menu-alias-fix">
                Fix unmatched partners…
              </DropdownMenuItem>
              <DropdownMenuItem onClick={openAliasBackfill}
                                data-testid="commissions-admin-alias-backfill">
                <Link2 className="h-4 w-4" /> Alias backfill
              </DropdownMenuItem>
              <DropdownMenuItem onClick={openAgentLinks}
                                data-testid="commissions-admin-agent-links">
                <Link2 className="h-4 w-4" /> Agent → Partner Success Manager linking
              </DropdownMenuItem>
              <DropdownMenuItem onClick={openMissingTax}
                                data-testid="commissions-admin-missing-tax">
                <Info className="h-4 w-4" /> Missing tax fields
              </DropdownMenuItem>
              <DropdownMenuItem onClick={openMissingEmail}
                                data-testid="commissions-admin-missing-email">
                <Info className="h-4 w-4" /> Missing contact email
              </DropdownMenuItem>
              <DropdownMenuItem onClick={openSandboxLog}
                                data-testid="commissions-admin-sandbox-log">
                <Info className="h-4 w-4" /> Sandbox log
              </DropdownMenuItem>
              <DropdownMenuItem onClick={openReminders}
                                data-testid="commissions-admin-reminders">
                <Info className="h-4 w-4" /> Payment reminders
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setDigestOpen(true)}
                                data-testid="commissions-admin-digest">
                <Info className="h-4 w-4" /> New deals digest (managers)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setWorkflowOpen(true)}
                                data-testid="commissions-admin-workflow">
                <Info className="h-4 w-4" /> Workflow recipients (Accounting, admins)
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={openDeletePeriod} disabled={!activePeriod}
                                data-testid="commissions-admin-delete-period"
                                className="text-rose-600 focus:text-rose-700 focus:bg-rose-50">
                <Trash2 className="h-4 w-4" /> Delete period…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </PageHeader>

      {sandboxOn && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
             data-testid="commissions-sandbox-banner">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <div className="flex-1">
            <b>SANDBOX mode</b> — no emails will actually send. Every Nudge, pro-forma
            email, and reminder is logged to the sandbox log so you can preview what
            would go out. Flip it off in <b>Admin → Payment reminders → Automatic monthly send</b>
            {" "}(the same dialog holds the Sandbox toggle) when you&rsquo;re ready to go live.
          </div>
          <button onClick={openSandboxLog}
                  data-testid="commissions-sandbox-banner-log"
                  className="rounded border border-amber-300 bg-white px-2 py-0.5 text-[11px] font-medium text-amber-900 hover:bg-amber-100">
            View log
          </button>
        </div>
      )}

      {/* Live view metrics — reflect whatever the current period + search +
          level filters resolve to, not all-time totals. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <StatCard label="Agents" value={viewStats.agents.toLocaleString()} icon={Users} />
        <StatCard label="Deals" value={viewStats.deals.toLocaleString()} icon={Package} />
        <StatCard label="Revenue" value={money(viewStats.revenue)} icon={BadgeDollarSign} />
        <StatCard label="Commissions" value={money(viewStats.commission)} icon={Sparkles} />
      </div>

      {loading ? (
        <Card className="border-slate-200 shadow-sm p-8 text-center text-slate-400">
          <Loader2 className="mx-auto h-4 w-4 animate-spin" />
        </Card>
      ) : periods.length === 0 ? (
        <Card className="border-slate-200 shadow-sm p-8 text-center">
          <DollarSign className="mx-auto h-8 w-8 text-slate-300 mb-2" />
          <div className="text-sm font-semibold text-slate-600">
            {isStaff ? "No commission periods yet" : "No commissions yet"}
          </div>
          <div className="mt-1 text-xs text-slate-400 mb-3">
            {isStaff
              ? "Commission data is pulled live from the billing platform — run a sync from the Commissions engine if nothing shows yet."
              : "Your commissions will appear here once the next billing period is synced."}
          </div>
        </Card>
      ) : (
        <>
          {/* Iter 306 — partner's pro forma: accept, ask, request review */}
          {!isStaff && <ProformaActionsCard periodLabel={activePeriodDoc?.period_label} />}
          {/* Period picker + summary + filters */}
          <Card className="border-slate-200 shadow-sm p-3 mb-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[240px]">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Period</Label>
                <Select value={activePeriod} onValueChange={(v) => { setActivePeriod(v); setRangeKey("single"); }}
                        disabled={rangeKey !== "single"}>
                  <SelectTrigger className="h-9" data-testid="commissions-period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {periods.map((p) => (
                      <SelectItem key={p.period_id} value={p.period_id}>
                        {p.period_label} — {p.row_count} agents, {money(p.total_commission)} commission
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[220px]">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Report range</Label>
                <Select value={rangeKey} onValueChange={setRangeKey}>
                  <SelectTrigger className="h-9" data-testid="commissions-range-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single" data-testid="range-opt-single">Single period (above)</SelectItem>
                    <SelectItem value="this_month">This month</SelectItem>
                    <SelectItem value="last_month">Last month</SelectItem>
                    <SelectItem value="this_quarter">This quarter</SelectItem>
                    <SelectItem value="last_quarter">Last quarter</SelectItem>
                    <SelectItem value="ytd">Year to date</SelectItem>
                    <SelectItem value="custom" data-testid="range-opt-custom">Custom…</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {rangeKey === "custom" && (
                <>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">From</Label>
                    <Input type="month" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                           className="h-9" data-testid="commissions-custom-from" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">To</Label>
                    <Input type="month" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                           className="h-9" data-testid="commissions-custom-to" />
                  </div>
                </>
              )}
              {rangeKey !== "single" && rangeSpec?.frm && rangeSpec?.to && (
                <div className="flex items-center gap-1.5 self-end">
                  <Button variant="outline" className="h-9" onClick={() => exportRange("csv")}
                          data-testid="range-export-csv">
                    <Download className="h-4 w-4" /> CSV
                  </Button>
                  <Button variant="outline" className="h-9" onClick={() => exportRange("pdf")}
                          data-testid="range-export-pdf">
                    <FileText className="h-4 w-4" /> PDF
                  </Button>
                  {has("reports.view") && (
                    <Button variant="outline" className="h-9" asChild data-testid="range-report-hub">
                      <Link to="/reports/commissions_range" title="Email now or schedule this report (Reports hub)">
                        <CalendarClock className="h-4 w-4" /> Schedule / email
                      </Link>
                    </Button>
                  )}
                </div>
              )}
            </div>
            {activePeriodDoc?.note && (
              <div className="mt-2 flex items-start gap-1.5 rounded-md bg-slate-50 border border-slate-100 px-2 py-1 text-xs text-slate-500">
                <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                {activePeriodDoc.note}
              </div>
            )}
          </Card>

          {/* Range report — replaces the single-period table when a preset
              or custom range is selected. */}
          {rangeKey !== "single" && (
            <Card className="border-slate-200 shadow-sm p-3 mb-4" data-testid="commissions-range-card">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-wider font-medium text-slate-500">
                  Range report {rangeSpec?.frm && rangeSpec?.to && <>· <b>{rangeSpec.frm}</b> → <b>{rangeSpec.to}</b></>}
                </div>
                {rangeData?.totals && (
                  <div className="text-xs text-slate-500 tabular-nums">
                    <b>{rangeData.totals.agents}</b> agents · <b>{rangeData.totals.deals}</b> deals ·
                    Revenue <b className="text-slate-700">{money(rangeData.totals.revenue)}</b> ·
                    Commission <b className="text-emerald-700">{money(rangeData.totals.commission)}</b>
                  </div>
                )}
              </div>
              {rangeLoad ? (
                <div className="py-10 text-center text-sm text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading…</div>
              ) : !rangeSpec?.frm || !rangeSpec?.to ? (
                <div className="py-10 text-center text-sm text-slate-400">Pick a &ldquo;From&rdquo; and &ldquo;To&rdquo; month above.</div>
              ) : (rangeData?.items || []).length === 0 ? (
                <div className="py-10 text-center text-sm text-slate-400" data-testid="commissions-range-empty">
                  No commission data in this range.
                </div>
              ) : (
                <div className="max-h-[calc(100vh-180px)] overflow-auto" data-testid="commissions-range-scroll">
                  <table className="w-full text-sm" data-testid="commissions-range-table">
                    <thead className="sticky top-0 z-10 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 shadow-[0_1px_0_0_rgb(226,232,240)]">
                      <tr>
                        <th className="px-3 py-2 text-center w-8">Hold</th>
                        <th className="px-3 py-2 text-left">Agent</th>
                        <th className="px-3 py-2 text-left">Territory</th>
                        <th className="px-3 py-2 text-left">PSM</th>
                        <th className="px-3 py-2 text-left">Level</th>
                        <th className="px-3 py-2 text-right">Periods</th>
                        <th className="px-3 py-2 text-right">Deals</th>
                        <th className="px-3 py-2 text-right">Revenue</th>
                        <th className="px-3 py-2 text-right">Commission</th>
                        <th className="px-3 py-2 text-right">Recurring</th>
                        <th className="px-3 py-2 text-right">One-time &amp; HW</th>
                        <th className="px-3 py-2 text-right">
                          <span title="Commission ÷ revenue on recurring services only — one-time charges, hardware and items paid at 0% are left out"
                                data-testid="range-rate-header-hint">Rate</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {rangeData.items.map((r, i) => (
                        <tr key={i} className="hover:bg-slate-50/50">
                          <td className="px-3 py-2 text-center">
                            <span title={r.paused ? "Paused" : "Active"}
                                  data-testid={`range-status-${i}`}
                                  className={`inline-block h-2.5 w-2.5 rounded-full ring-2 ring-white
                                              ${r.paused
                                                ? "bg-rose-500 shadow-[0_0_0_2px_rgba(244,63,94,0.25)]"
                                                : "bg-emerald-500 shadow-[0_0_0_2px_rgba(16,185,129,0.25)]"}`} />
                          </td>
                          <td className="px-3 py-2 font-medium text-slate-800">
                            <Link to={`/commissions/partners/${encodeURIComponent(r.agent)}`}
                                  className="hover:text-primary hover:underline"
                                  data-testid={`range-agent-${i}`}>
                              {r.agent}
                            </Link>
                          </td>
                          <td className="px-3 py-2 text-xs text-slate-500">{r.partner}</td>
                          <td className="px-3 py-2 text-xs text-slate-500">{r.psm}</td>
                          <td className="px-3 py-2 text-xs">{r.level}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-500">{r.periods}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{r.deals}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-700">{money(r.revenue)}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium text-emerald-700">{money(r.commission)}</td>
                          <td className="px-3 py-2 text-right"><SplitMoney value={r.recurring_commission} testid={`range-recurring-${i}`} /></td>
                          <td className="px-3 py-2 text-right"><SplitMoney value={r.onetime_commission} muted testid={`range-onetime-${i}`} /></td>
                          <td className="px-3 py-2 text-right">
                            <AvgRateCell rate={r.commission_rate} row={r} level={r.level} isStaff={isStaff} testid={`range-rate-${i}`} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}

          {rangeKey === "single" && (
          <>
          {/* Filters */}
          <Card className="border-slate-200 shadow-sm p-3 mb-4">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)}
                       placeholder="Search agent, partner, PSM…"
                       className="pl-7 h-9" data-testid="commissions-search" />
              </div>
              <div className="flex items-center gap-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Level</Label>
                <Select value={levelFilter} onValueChange={setLevelFilter}>
                  <SelectTrigger className="h-9 min-w-[130px]" data-testid="commissions-level-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    {LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">PSM</Label>
                <Select value={psmFilter} onValueChange={setPsmFilter}>
                  <SelectTrigger className="h-9 min-w-[130px]" data-testid="commissions-psm-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    {psmOptions.map((p) => (
                      <SelectItem key={p} value={p} data-testid={`commissions-psm-option-${p}`}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <PageSizeSelect pageSize={pg.pageSize} setPageSize={pg.setPageSize} testid="commissions-page-size" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9" data-testid="commissions-columns-button">
                    <Columns3 className="h-4 w-4" /> Columns
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 max-h-[420px] overflow-y-auto">
                  <DropdownMenuLabel>Show columns</DropdownMenuLabel>
                  {order.map((k) => (
                    <DropdownMenuCheckboxItem key={k} checked={!hidden.includes(k)}
                      onCheckedChange={() => toggleHidden(k)}
                      onSelect={(e) => e.preventDefault()}
                      data-testid={`commissions-col-toggle-${k}`}>
                      {COL_MAP[k].label}
                    </DropdownMenuCheckboxItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={resetCols} data-testid="commissions-col-reset">
                    Reset columns
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <span className="text-xs text-slate-400 tabular-nums ml-auto" data-testid="commissions-count">
                {filtered.length.toLocaleString()} of {rows.length.toLocaleString()}
              </span>
            </div>
          </Card>

          {/* Rows table */}
          <Card className="border-slate-200 shadow-sm overflow-hidden">
            <div className="max-h-[calc(100vh-180px)] overflow-auto" data-testid="commissions-table-scroll">
              <table className="w-full text-sm" data-testid="commissions-main-table">
                <thead className="sticky top-0 z-10 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 shadow-[0_1px_0_0_rgb(226,232,240)]">
                  <tr>
                    {visibleCols.map((c) => (
                      <th key={c.key}
                          className={`px-3 py-2 whitespace-nowrap ${c.align === "right" ? "text-right" : "text-left"}`}>
                        <button onClick={() => c.sortable && toggleSort(c.key)}
                                className={`inline-flex items-center gap-1 ${c.sortable ? "hover:text-slate-800 cursor-pointer" : "cursor-default"}`}
                                data-testid={`commissions-sort-${c.key}`}>
                          {c.key === "standard_commission_rate"
                            ? <span title="Commission ÷ revenue on recurring services only — one-time charges, hardware and items paid at 0% are left out" data-testid="avg-rate-header-hint">{c.label}</span>
                            : c.label}
                          {sort.key === c.key && (
                            sort.dir === "asc"
                              ? <ArrowUp className="h-3 w-3 text-primary" />
                              : <ArrowDown className="h-3 w-3 text-primary" />
                          )}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rowsLoading ? (
                    <tr><td colSpan={visibleCols.length} className="px-3 py-8 text-center text-slate-400">
                      <Loader2 className="mx-auto h-4 w-4 animate-spin" /></td></tr>
                  ) : pg.pageItems.length === 0 ? (
                    <tr><td colSpan={visibleCols.length} className="px-3 py-12 text-center text-sm text-slate-400">
                      No rows match the current filters.
                    </td></tr>
                  ) : pg.pageItems.map((r) => (
                    <tr key={r.row_id} data-testid={`commissions-row-${r.row_id}`}
                        className="hover:bg-slate-50/50">
                      {visibleCols.map((c) => (
                        <td key={c.key}
                            className={`px-3 py-2 whitespace-nowrap ${c.align === "right" ? "text-right" : ""}`}>
                          {c.render(r, { setDrilldown, historyMap, isStaff })}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TablePagination {...pg} testid="commissions-pagination" />
          </Card>
          {!isStaff && <MyDealsCard periodLabel={activePeriodDoc?.period_label} />}
          </>
          )}
        </>
      )}

      {uploadOpen && <UploadDialog onClose={() => setUploadOpen(false)} onDone={loadPeriods} />}
      {drilldown && <AgentDrilldown agent={drilldown} onClose={() => setDrilldown(null)} />}
      <CommissionAliasFixDialog open={aliasFix} onOpenChange={setAliasFix} />
      {aliasDialog && (
        <AliasBackfillDialog state={aliasDialog}
                             tourStep={tourStep === 3 ? "3/3" : ""}
                             tourNextLabel="Finish tour"
                             onTourNext={tourStep === 3 ? () => {
                               setAliasDialog(null);
                               setTourStep(0);
                               toast.success("Onboarding tour complete 🎉");
                             } : null}
                             onApply={applyAliasBackfill}
                             onClose={() => { setAliasDialog(null); if (tourStep === 3) setTourStep(0); }} />
      )}
      {digestOpen && <NewDealDigestDialog onClose={() => setDigestOpen(false)} />}
      {workflowOpen && <WorkflowConfigDialog onClose={() => setWorkflowOpen(false)} />}
      {remindersDialog && (
        <RemindersDialog state={remindersDialog}
                         onSave={saveReminders}
                         onPreview={previewReminders}
                         onSend={sendReminders}
                         onToggle={(k) => setRemindersDialog((s) => ({ ...s, unticked: (s.unticked || []).includes(k)
                           ? s.unticked.filter((x) => x !== k) : [...(s.unticked || []), k] }))}
                         onEmailAdded={previewReminders}
                         onClose={() => setRemindersDialog(null)} />
      )}
      {agentLinksDialog && (
        <AgentLinksDialog state={agentLinksDialog}
                          onSave={saveAgentLink}
                          onSearchChange={(q) => setAgentLinksDialog((s) => ({ ...(s || {}), query: q }))}
                          onClose={() => setAgentLinksDialog(null)} />
      )}
      {missingTaxDialog && (
        <MissingTaxDialog state={missingTaxDialog}
                          tourStep={tourStep === 1 ? "1/3" : ""}
                          tourNextLabel="Next: contact email"
                          onTourNext={tourStep === 1 ? () => {
                            setMissingTaxDialog(null);
                            setTourStep(2);
                            openMissingEmail();
                          } : null}
                          onChangeRow={updateMissingRow}
                          onSaveRow={saveMissingTax}
                          onClose={() => { setMissingTaxDialog(null); if (tourStep === 1) setTourStep(0); }} />
      )}
      {missingEmailDialog && (
        <MissingEmailDialog state={missingEmailDialog}
                            tourStep={tourStep === 2 ? "2/3" : ""}
                            tourNextLabel="Next: alias backfill"
                            onTourNext={tourStep === 2 ? () => {
                              setMissingEmailDialog(null);
                              setTourStep(3);
                              openAliasBackfill();
                            } : null}
                            onChangeRow={updateMissingEmailRow}
                            onSaveRow={saveMissingEmail}
                            onClose={() => { setMissingEmailDialog(null); if (tourStep === 2) setTourStep(0); }} />
      )}
      {sandboxLogDialog && (
        <SandboxLogDialog state={sandboxLogDialog}
                          onReload={loadSandboxLog}
                          onClose={() => setSandboxLogDialog(null)} />
      )}

      <Dialog open={!!deleteDialog} onOpenChange={(o) => { if (!o && !deleteBusy) { setDeleteDialog(null); setDeleteTyped(""); } }}>
        <DialogContent className="border-rose-300" data-testid="commissions-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="h-5 w-5" /> Delete period — dangerous
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              This permanently deletes <b className="text-rose-700">{deleteDialog?.period_label}</b> and
              every commission row and deal for that month — affecting <b>many partners and agents</b>.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
            Deletion is not immediate. Submitting sends a one-time approval link to{" "}
            <b>keith@unitedcloud.ca</b>. The period is only removed once he approves it from his inbox.
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="delete-period-confirm" className="text-xs text-slate-600">
              Type the period <b className="font-mono">{deleteDialog?.period_label}</b> to confirm
            </Label>
            <Input id="delete-period-confirm" value={deleteTyped}
                   onChange={(e) => setDeleteTyped(e.target.value)}
                   placeholder={deleteDialog?.period_label || ""}
                   autoComplete="off" data-testid="commissions-delete-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteDialog(null); setDeleteTyped(""); }}
                    disabled={deleteBusy} data-testid="commissions-delete-cancel">Cancel</Button>
            <Button onClick={submitDeletePeriod}
                    disabled={deleteBusy || deleteTyped.trim() !== (deleteDialog?.period_label || "")}
                    className="bg-rose-600 text-white hover:bg-rose-700"
                    data-testid="commissions-delete-submit">
              {deleteBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Request deletion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---- Small UI helpers -------------------------------------------------

function StatCard({ label, value, icon: Icon }) {
  return (
    <Card className="border-slate-200 shadow-sm p-3 flex items-center gap-3">
      <div className="rounded-lg bg-primary/10 p-2 text-primary"><Icon className="h-4 w-4" /></div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 truncate">{label}</div>
        <div className="text-lg font-semibold text-slate-800 tabular-nums">{value}</div>
      </div>
    </Card>
  );
}

// Commission split cell — "—" for months uploaded before the split existed.
function SplitMoney({ value, muted = false, testid }) {
  if (value === undefined || value === null) return <span className="text-slate-300" data-testid={testid}>—</span>;
  return <span className={`tabular-nums ${muted ? "text-slate-500" : "text-slate-700"}`} data-testid={testid}>{money(value)}</span>;
}

// Avg Rate, with a staff-only amber flag when it beats the level's top regular rate.
function AvgRateCell({ rate, row, level, isStaff, testid }) {
  const above = isStaff && row?.above_level;
  return (
    <span className={`inline-flex items-center gap-1 tabular-nums text-xs ${above ? "rounded bg-amber-50 px-1.5 py-0.5 font-semibold text-amber-700 ring-1 ring-amber-200" : "text-slate-500"}`}
          title={above ? `Above ${level || row.level_name}'s top regular rate (${row.level_top_rate}%)` : undefined}
          data-testid={testid}>
      {above && <AlertTriangle className="h-3 w-3" data-testid={`${testid}-above-level`} />}
      {(rate || 0).toFixed(2)}%
    </span>
  );
}

// Tiny inline SVG sparkline — one value per period for one agent (commission by default).
// Points are `[{period_label, commission, recurring_revenue}, ...]` sorted oldest → newest.
function Sparkline({ points: raw, width = 72, height = 22, field = "commission", label = "Commission", testid = "sparkline" }) {
  const points = (raw || []).filter((p) => p[field] !== undefined && p[field] !== null);
  if (points.length < 2) {
    return <span className="text-[10px] text-slate-300" data-testid={`${testid}-empty`}>—</span>;
  }
  const vals = points.map((p) => Number(p[field]) || 0);
  const max = Math.max(...vals), min = Math.min(...vals);
  const range = Math.max(max - min, 1);
  const step = width / (points.length - 1);
  const coords = vals.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const last = vals[vals.length - 1];
  const prev = vals[vals.length - 2];
  const stroke = last >= prev ? "#059669" : "#ef4444";
  return (
    <svg width={width} height={height} className="inline-block align-middle"
         data-testid={testid}
         aria-label={`${label} — ` + points.map((p, i) => `${p.period_label}: $${vals[i].toFixed(2)}`).join(" · ")}>
      <title>{`${label} — ` + points.map((p, i) => `${p.period_label}: $${vals[i].toFixed(2)}`).join(" · ")}</title>
      <polyline fill="none" stroke={stroke} strokeWidth="1.5" points={coords} />
      <circle cx={(points.length - 1) * step}
              cy={height - ((last - min) / range) * (height - 4) - 2}
              r="2" fill={stroke} />
    </svg>
  );
}


function LevelPill({ level }) {
  if (!level) return <span className="text-slate-300">—</span>;
  const l = (level || "").toLowerCase();
  // Colour map — Gold > Silver > Bronze > Platinum in one glance.
  const cls = l === "gold"     ? "bg-yellow-100 text-yellow-800"
            : l === "silver"   ? "bg-slate-200 text-slate-700"
            : l === "bronze"   ? "bg-orange-100 text-orange-800"
            : l === "platinum" ? "bg-indigo-100 text-indigo-800"
            : l === "no commission" ? "bg-red-50 text-red-700 ring-1 ring-red-200"
            :                    "bg-slate-100 text-slate-500";
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${cls}`}>{level}</span>;
}


// ---- Upload dialog ---------------------------------------------------

function UploadDialog({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [period, setPeriod] = useState("");
  const [note, setNote] = useState("");
  const [replace, setReplace] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  // Auto-suggest period from filename on file select (server has the regex).
  const pickFile = async (f) => {
    setFile(f);
    if (!period && f) {
      try {
        const { data } = await api.post("/commissions/suggest-period", { filename: f.name });
        if (data.period) setPeriod(data.period);
      } catch { /* soft-fail */ }
    }
  };

  const doUpload = async () => {
    if (!file)   { toast.error("Pick a CSV first"); return; }
    if (!/^\d{4}-\d{2}$/.test(period.trim())) { toast.error("Period must be YYYY-MM"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("period", period.trim());
      fd.append("note", note);
      fd.append("replace", replace ? "true" : "false");
      const { data } = await api.post("/commissions/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.row_count} rows for ${data.period_label} (${money(data.total_commission)} commission)`);
      onDone(); onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="commissions-upload-dialog">
        <DialogHeader>
          <DialogTitle>Upload commissions CSV</DialogTitle>
          <DialogDescription>
            Ingest a monthly Stax Bill Commission Report. One row per agent —
            <code className="text-xs">sequence, territory, agent, psm_name, level_name, deals, total, commission, standard_commission_rate</code>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div
            onClick={() => inputRef.current?.click()}
            className="rounded-lg border-2 border-dashed border-slate-200 hover:border-slate-300 p-6 text-center cursor-pointer"
            data-testid="commissions-upload-dropzone">
            <Upload className="mx-auto h-6 w-6 text-slate-400 mb-1" />
            {file ? (
              <div className="text-sm text-slate-700 font-medium">{file.name}
                <span className="ml-2 text-xs text-slate-400">({(file.size / 1024).toFixed(1)} KB)</span>
              </div>
            ) : (
              <div className="text-sm text-slate-600">Click to choose a CSV</div>
            )}
            <input ref={inputRef} type="file" accept=".csv,text/csv"
                   onChange={(e) => pickFile(e.target.files?.[0] || null)}
                   className="hidden" data-testid="commissions-upload-file" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] text-slate-500 mb-1 block">Period (YYYY-MM) *</Label>
              <Input value={period} onChange={(e) => setPeriod(e.target.value)}
                     placeholder="2026-07" data-testid="commissions-upload-period" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)}
                       data-testid="commissions-upload-replace" />
                Overwrite if period already exists
              </label>
            </div>
          </div>
          <div>
            <Label className="text-[11px] text-slate-500 mb-1 block">Note (optional)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder="Anything worth remembering about this import — corrections applied, missing agents…"
                      data-testid="commissions-upload-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="commissions-upload-cancel">
            <X className="h-4 w-4" /> Cancel
          </Button>
          <Button onClick={doUpload} disabled={!file || uploading}
                  data-testid="commissions-upload-submit"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---- Per-agent drill-down --------------------------------------------

function AgentDrilldown({ agent, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get(`/commissions/agents/${encodeURIComponent(agent)}`);
        setData(d);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load agent history");
      }
    })();
  }, [agent]);

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="commissions-drilldown">
        <DialogHeader>
          <DialogTitle>{cleanAgent(agent)}</DialogTitle>
          <DialogDescription>
            All-time commissions history — one row per period.
          </DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="py-8 text-center text-slate-400"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <StatCard label="Lifetime deals"      value={data.lifetime.deals.toLocaleString()}     icon={Package} />
              <StatCard label="Lifetime revenue"    value={money(data.lifetime.revenue)}              icon={BadgeDollarSign} />
              <StatCard label="Lifetime commission" value={money(data.lifetime.commission)}          icon={Sparkles} />
            </div>
            <div className="max-h-[380px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left">Period</th>
                    <th className="px-3 py-2 text-left">Territory</th>
                    <th className="px-3 py-2 text-left">Level</th>
                    <th className="px-3 py-2 text-right">Deals</th>
                    <th className="px-3 py-2 text-right">Revenue</th>
                    <th className="px-3 py-2 text-right">Commission</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.items.map((r) => (
                    <tr key={r.row_id} className="hover:bg-slate-50/50">
                      <td className="px-3 py-2 font-medium">{r.period_label}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{r.territory || "—"}</td>
                      <td className="px-3 py-2"><LevelPill level={r.level_name} /></td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.deals}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(r.total)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium text-emerald-700">{money(r.commission)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.items.length === 0 && (
                <div className="py-8 text-center text-sm text-slate-400">No history for this agent yet.</div>
              )}
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="commissions-drilldown-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AliasBackfillDialog({ state, onApply, onClose, tourStep, tourNextLabel, onTourNext }) {
  const preview = state?.preview;
  const result  = state?.result;
  const matched   = (result || preview)?.matched   || [];
  const conflicts = (result || preview)?.conflicts || [];
  const unmatched = (result || preview)?.unmatched_agents || [];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="alias-backfill-dialog">
        <DialogHeader>
          <DialogTitle>Alias backfill</DialogTitle>
          <DialogDescription>
            Scans the agent strings in commission data and fuzzy-matches them to
            partner orgs. Existing aliases are never overwritten.
          </DialogDescription>
        </DialogHeader>
        {state?.loading || (!preview && !result) ? (
          <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" /></div>
        ) : (
          <div className="overflow-y-auto flex-1 -mx-6 px-6 space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <MiniStat label="Would-match" value={matched.length}   tone="emerald" testid="alias-count-matched" />
              <MiniStat label="Conflicts"   value={conflicts.length} tone="rose"    testid="alias-count-conflicts" />
              <MiniStat label="Unmatched"   value={unmatched.length} tone="slate"   testid="alias-count-unmatched" />
            </div>
            {matched.length > 0 && (
              <Section title="New aliases" testid="alias-matched-list">
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                    <tr><th className="text-left py-1">Org</th><th className="text-left py-1">Alias to set</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {matched.slice(0, 50).map((m) => (
                      <tr key={m.org_id}><td className="py-1 pr-2">{m.org_name}</td><td className="py-1 text-slate-500">{m.alias}</td></tr>
                    ))}
                  </tbody>
                </table>
                {matched.length > 50 && <div className="text-[10px] text-slate-400 mt-1">…and {matched.length - 50} more</div>}
              </Section>
            )}
            {conflicts.length > 0 && (
              <Section title="Conflicts (need manual review)" tone="rose" testid="alias-conflicts-list">
                <ul className="space-y-1 text-xs">
                  {conflicts.map((c, i) => (
                    <li key={i} className="text-rose-700">
                      <b>{c.org_name}</b> — claimed by: <span className="text-slate-500">{c.agents.join(" · ")}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}
            {unmatched.length > 0 && (
              <Section title="Unmatched agents" testid="alias-unmatched-list">
                <div className="text-xs text-slate-500 leading-relaxed">
                  {unmatched.slice(0, 30).join(" · ")}{unmatched.length > 30 && "…"}
                </div>
              </Section>
            )}
            {result && (
              <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800"
                   data-testid="alias-result-applied">
                Backfill applied — {result.matched?.length || 0} aliases written.
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          {tourStep && (
            <div className="mr-auto flex items-center gap-2 text-xs text-slate-500" data-testid="tour-footer-alias">
              <span className="rounded bg-primary/10 px-2 py-1 font-semibold text-primary">Tour · Step {tourStep}</span>
              <span>One click backfills every match</span>
            </div>
          )}
          <Button variant="outline" onClick={onClose} data-testid="alias-cancel">Close</Button>
          {!result && preview && matched.length > 0 && (
            <Button onClick={onApply} disabled={state?.applying}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    data-testid="alias-apply">
              {state?.applying ? <Loader2 className="h-4 w-4 animate-spin" /> : `Apply ${matched.length} aliases`}
            </Button>
          )}
          {onTourNext && (
            <Button onClick={onTourNext}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="alias-tour-next">
              {tourNextLabel || "Finish"} ✓
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RemindersDialog({ state, onSave, onPreview, onSend, onToggle, onEmailAdded, onClose }) {
  const c = state?.cfg || {};
  const [enabled, setEnabled] = useState(!!c.enabled);
  const [sandbox, setSandbox] = useState(c.sandbox !== false);   // default ON
  const [day,     setDay]     = useState(c.day_of_month || 15);
  const [hour,    setHour]    = useState(c.hour ?? 9);
  const [minAmt,  setMinAmt]  = useState(c.min_amount ?? 1);
  const [cc,      setCc]      = useState((c.cc || []).join(", "));
  const [prefix,  setPrefix]  = useState(c.subject_prefix || "Commission payment update");
  useEffect(() => {
    setEnabled(!!c.enabled);
    setSandbox(c.sandbox !== false);
    setDay(c.day_of_month || 15);
    setHour(c.hour ?? 9); setMinAmt(c.min_amount ?? 1);
    setCc((c.cc || []).join(", ")); setPrefix(c.subject_prefix || "Commission payment update");
  }, [c.enabled, c.sandbox, c.day_of_month, c.hour, c.min_amount, c.subject_prefix]);
  const save = () => onSave({
    enabled, sandbox,
    day_of_month: Number(day), hour: Number(hour),
    min_amount: Number(minAmt),
    cc: cc.split(",").map((s) => s.trim()).filter(Boolean),
    subject_prefix: prefix,
  });
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="reminders-dialog">
        <DialogHeader>
          <DialogTitle>Payment reminders</DialogTitle>
          <DialogDescription>
            Monthly nudge email to partners with a positive net payable — sent from the same address as the pro-forma.
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto flex-1 -mx-6 px-6 space-y-4">
          <label className={`flex items-start gap-2 rounded border p-3 cursor-pointer ${sandbox ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`}
                 data-testid="reminders-sandbox-row">
            <input type="checkbox" checked={sandbox} onChange={(e) => setSandbox(e.target.checked)}
                   className="mt-1" data-testid="reminders-sandbox-toggle" />
            <div className="text-sm">
              <div className="font-medium text-slate-800">
                Sandbox mode {sandbox
                  ? <span className="ml-1 rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900">ON — safe</span>
                  : <span className="ml-1 rounded bg-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-900">OFF — will send live</span>}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                While ON, every Nudge / pro-forma email / reminder is logged to the sandbox log instead of being sent through Resend. Turn OFF to actually deliver mail.
              </div>
            </div>
          </label>
          <label className="flex items-start gap-2 rounded border border-slate-200 bg-slate-50 p-3 cursor-pointer"
                 data-testid="reminders-enabled-row">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
                   className="mt-1" data-testid="reminders-enabled" />
            <div className="text-sm">
              <div className="font-medium text-slate-800">Automatic monthly send</div>
              <div className="text-xs text-slate-500 mt-0.5">
                When ON, the cron fires each month on the day + hour below (America/Edmonton).
                Manual &ldquo;Send now&rdquo; always works regardless of this flag.
              </div>
            </div>
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Day of month</Label>
              <Input type="number" min={1} max={28} value={day}
                     onChange={(e) => setDay(e.target.value)}
                     data-testid="reminders-day" />
            </div>
            <div>
              <Label className="text-xs">Hour (24h)</Label>
              <Input type="number" min={0} max={23} value={hour}
                     onChange={(e) => setHour(e.target.value)}
                     data-testid="reminders-hour" />
            </div>
            <div>
              <Label className="text-xs">Min amount</Label>
              <Input type="number" step="0.01" min={0} value={minAmt}
                     onChange={(e) => setMinAmt(e.target.value)}
                     data-testid="reminders-min-amount" />
            </div>
            <div>
              <Label className="text-xs">Subject prefix</Label>
              <Input value={prefix} onChange={(e) => setPrefix(e.target.value)}
                     data-testid="reminders-subject-prefix" />
            </div>
          </div>
          <div>
            <Label className="text-xs">CC (comma-separated)</Label>
            <Input value={cc} onChange={(e) => setCc(e.target.value)}
                   placeholder="finance-ops@unitedcloud.ca"
                   data-testid="reminders-cc" />
          </div>
          {c.last_run_at && (
            <div className="rounded border border-slate-200 bg-white p-3 text-xs">
              <div className="font-medium text-slate-700 mb-1">Last run · {c.last_run_at.slice(0, 16).replace("T", " ")}</div>
              {c.last_run_summary && typeof c.last_run_summary === "object" ? (
                <div className="text-slate-500 space-y-0.5">
                  <div>Period {c.last_run_summary.period_label} · trigger {c.last_run_summary.trigger}</div>
                  <div>Sent {c.last_run_summary.sent?.length || 0} · failed {c.last_run_summary.failed?.length || 0} · skipped (no email) {c.last_run_summary.skipped_no_email?.length || 0}</div>
                </div>
              ) : null}
            </div>
          )}
          {state?.dryRun && (
            <div className="rounded border border-sky-200 bg-sky-50 p-3 text-xs" data-testid="reminders-dryrun-summary">
              <div className="font-medium text-sky-800">
                {state.dryRun.dry_run ? "Preview" : "Live send"} · {state.dryRun.period_label}
              </div>
              <div className="mt-1 text-sky-800">
                {state.dryRun.eligible_count} eligible ·
                {" "}sent {state.dryRun.sent?.length || 0} ·
                {" "}failed {state.dryRun.failed?.length || 0} ·
                {" "}skipped (no email) {state.dryRun.skipped_no_email?.length || 0}
              </div>
              <ReminderRecipients result={state.dryRun} unticked={state.unticked || []}
                                  onToggle={onToggle} onEmailAdded={onEmailAdded} />
              {(state.dryRun.failed || []).length > 0 && (
                <ul className="mt-2 space-y-0.5 text-rose-700">
                  {state.dryRun.failed.map((f, i) => (<li key={i}>{f.partner}: {f.error}</li>))}
                </ul>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="reminders-close">Close</Button>
          <Button variant="outline" onClick={onPreview} disabled={state?.sending}
                  data-testid="reminders-preview">
            {state?.sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Preview eligibility"}
          </Button>
          <Button variant="outline" onClick={onSend} disabled={state?.sending}
                  className="text-sky-700 border-sky-200 hover:bg-sky-50"
                  data-testid="reminders-send-now">
            {state?.sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send now"}
          </Button>
          <Button onClick={save} disabled={state?.saving}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  data-testid="reminders-save">
            {state?.saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MiniStat({ label, value, tone = "slate", testid }) {
  const toneClass = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    rose:    "border-rose-200 bg-rose-50 text-rose-700",
    slate:   "border-slate-200 bg-slate-50 text-slate-700",
  }[tone];
  return (
    <div className={`rounded border p-2.5 ${toneClass}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function Section({ title, children, tone = "slate", testid }) {
  const border = tone === "rose" ? "border-rose-200" : "border-slate-200";
  return (
    <div className={`rounded border ${border} p-3`} data-testid={testid}>
      <div className="text-xs font-semibold text-slate-700 mb-2">{title}</div>
      {children}
    </div>
  );
}

function AgentLinksDialog({ state, onSave, onSearchChange, onClose }) {
  const q = (state?.query || "").toLowerCase().trim();
  const filteredUnlinked = (state?.unlinked || []).filter(
    (a) => !q || a.toLowerCase().includes(q));
  const filteredLinks = (state?.links || []).filter(
    (l) => !q || l.agent?.toLowerCase().includes(q));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="agent-links-dialog">
        <DialogHeader>
          <DialogTitle>Agent → Partner Success Manager linking</DialogTitle>
          <DialogDescription>
            Map each agent name from the CSV to a Partner Success Manager. Linked Partner Success
            Managers get a personal &ldquo;My commissions&rdquo; tile on their dashboard covering just their own numbers.
          </DialogDescription>
        </DialogHeader>
        {state?.loading || !state?.users ? (
          <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" /></div>
        ) : (
          <div className="overflow-y-auto flex-1 -mx-6 px-6 space-y-4">
            <Input placeholder="Search agents by name…"
                   value={state.query || ""}
                   onChange={(e) => onSearchChange(e.target.value)}
                   data-testid="agent-links-search" />
            {filteredLinks.length > 0 && (
              <Section title={`Already linked (${filteredLinks.length})`} testid="agent-links-linked">
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                    <tr><th className="text-left py-1">Agent</th><th className="text-left py-1">Partner Success Manager</th><th></th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredLinks.map((l) => {
                      const u = state.users.find((x) => x.user_id === l.user_id);
                      return (
                        <tr key={l.agent} data-testid={`agent-link-row-${l.agent}`}>
                          <td className="py-1.5 pr-2 text-slate-700">{decodeHtmlEntities(l.agent)}</td>
                          <td className="py-1.5 pr-2 text-slate-600">
                            {u ? `${u.name} — ${u.email}` : <span className="text-slate-400 italic">unknown Partner Success Manager</span>}
                          </td>
                          <td className="py-1.5 text-right">
                            <button onClick={() => onSave(l.agent, "")}
                                    className="text-[10px] text-rose-600 hover:underline"
                                    data-testid={`agent-unlink-${l.agent}`}>
                              Unlink
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Section>
            )}
            {filteredUnlinked.length > 0 && (
              <Section title={`Unlinked agents (${filteredUnlinked.length})`} testid="agent-links-unlinked">
                <div className="space-y-1.5">
                  {filteredUnlinked.map((agent) => (
                    <UnlinkedAgentRow key={agent} agent={agent}
                                      users={state.users}
                                      onLink={(uid) => onSave(agent, uid)} />
                  ))}
                </div>
              </Section>
            )}
            {filteredLinks.length === 0 && filteredUnlinked.length === 0 && (
              <div className="py-8 text-center text-sm text-slate-400">No agents match &ldquo;{q}&rdquo;.</div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="agent-links-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UnlinkedAgentRow({ agent, users, onLink }) {
  const [sel, setSel] = useState("");
  return (
    <div className="flex items-center gap-2 rounded border border-slate-200 bg-white p-2"
         data-testid={`unlinked-agent-row-${agent}`}>
      <span className="flex-1 text-xs text-slate-700 truncate">{decodeHtmlEntities(agent)}</span>
      <select value={sel} onChange={(e) => setSel(e.target.value)}
              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs max-w-[220px]"
              data-testid={`unlinked-agent-select-${agent}`}>
        <option value="">Choose a Partner Success Manager…</option>
        {users.map((u) => (
          <option key={u.user_id} value={u.user_id}>
            {u.name} — {u.email}
          </option>
        ))}
      </select>
      <button onClick={() => sel && onLink(sel)} disabled={!sel}
              className="rounded bg-primary px-2 py-1 text-[10px] font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
              data-testid={`unlinked-agent-link-${agent}`}>
        Link
      </button>
    </div>
  );
}


function MissingTaxDialog({ state, onChangeRow, onSaveRow, onClose, tourStep, tourNextLabel, onTourNext }) {
  const rows = state?.rows || [];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="missing-tax-dialog">
        <DialogHeader>
          <DialogTitle>Partners missing tax fields</DialogTitle>
          <DialogDescription>
            Every partner with commission activity but no tax rate / jurisdiction on file.
            Fill both here and hit Save — the numbers flow straight into their next pro-forma.
          </DialogDescription>
        </DialogHeader>
        {state?.loading ? (
          <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" /></div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center">
            <div className="text-emerald-600 font-medium">All caught up 🎉</div>
            <div className="mt-1 text-xs text-slate-500">
              {state?.complete_count || 0} partner{(state?.complete_count === 1) ? "" : "s"} with commission activity all have tax profiles.
            </div>
          </div>
        ) : (
          <div className="overflow-y-auto flex-1 -mx-6 px-6 space-y-2">
            <div className="text-xs text-slate-500">
              <b>{rows.length}</b> partner{rows.length === 1 ? "" : "s"} missing tax details ·
              {" "}<b className="text-emerald-700">{state?.complete_count || 0}</b> already complete
            </div>
            <table className="w-full text-xs" data-testid="missing-tax-table">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 pr-2">Partner</th>
                  <th className="text-left py-2 pr-2 w-24">Rate %</th>
                  <th className="text-left py-2 pr-2 w-48">Jurisdiction</th>
                  <th className="text-left py-2 pr-2 w-32">Tax reg #</th>
                  <th className="text-right py-2 pr-2 w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.org_id} data-testid={`missing-tax-row-${r.org_id}`}>
                    <td className="py-2 pr-2 align-top">
                      <div className="font-medium text-slate-800">{r.name}</div>
                      <div className="text-[10px] text-slate-400">
                        {r.partner_type || "—"}
                        {r.commission_alias && r.commission_alias !== r.name && (
                          <> · alias {r.commission_alias}</>
                        )}
                      </div>
                    </td>
                    <td className="py-2 pr-2 align-top">
                      <Input type="number" step="0.001" min={0} max={100}
                             value={r._tax_rate}
                             onChange={(e) => onChangeRow(r.org_id, { _tax_rate: e.target.value })}
                             placeholder="5.00"
                             className="h-7 text-xs"
                             data-testid={`missing-tax-rate-${r.org_id}`} />
                    </td>
                    <td className="py-2 pr-2 align-top">
                      <select value={CA_TAX_PRESETS.find((p) => p.value === r._tax_jurisdiction)?.code
                                       || (r._tax_jurisdiction ? "OTHER" : "")}
                              onChange={(e) => {
                                const code = e.target.value;
                                if (code === "") {
                                  onChangeRow(r.org_id, { _tax_jurisdiction: "", _tax_rate: "" });
                                } else if (code === "OTHER") {
                                  onChangeRow(r.org_id, { _tax_jurisdiction: "" });
                                } else {
                                  const p = CA_TAX_PRESETS.find((x) => x.code === code);
                                  if (p) onChangeRow(r.org_id, { _tax_jurisdiction: p.value, _tax_rate: p.rate });
                                }
                              }}
                              className="h-7 w-full rounded border border-slate-200 bg-white px-1.5 text-xs"
                              data-testid={`missing-tax-jurisdiction-select-${r.org_id}`}>
                        <option value="">Choose…</option>
                        {CA_TAX_PRESETS.map((p) => (
                          <option key={p.code} value={p.code}>{p.label}</option>
                        ))}
                        <option value="OTHER">Custom / Other</option>
                      </select>
                      {(!CA_TAX_PRESETS.some((p) => p.value === r._tax_jurisdiction) && r._tax_jurisdiction !== "") && (
                        <Input value={r._tax_jurisdiction}
                               onChange={(e) => onChangeRow(r.org_id, { _tax_jurisdiction: e.target.value })}
                               placeholder="e.g. NY · Sales Tax"
                               className="h-7 text-xs mt-1"
                               data-testid={`missing-tax-jurisdiction-${r.org_id}`} />
                      )}
                    </td>
                    <td className="py-2 pr-2 align-top">
                      <Input value={r._tax_number}
                             onChange={(e) => onChangeRow(r.org_id, { _tax_number: e.target.value })}
                             placeholder="Optional"
                             className="h-7 text-xs"
                             data-testid={`missing-tax-number-${r.org_id}`} />
                    </td>
                    <td className="py-2 pr-2 text-right align-top">
                      <Button size="sm" onClick={() => onSaveRow(r)}
                              disabled={state?.savingOrgId === r.org_id}
                              className="h-7 bg-primary text-primary-foreground hover:bg-primary/90"
                              data-testid={`missing-tax-save-${r.org_id}`}>
                        {state?.savingOrgId === r.org_id
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : "Save"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          {tourStep && (
            <div className="mr-auto flex items-center gap-2 text-xs text-slate-500" data-testid="tour-footer-missing-tax">
              <span className="rounded bg-primary/10 px-2 py-1 font-semibold text-primary">Tour · Step {tourStep}</span>
              <span>Fill (or skip) rows, then continue</span>
            </div>
          )}
          <Button variant="outline" onClick={onClose} data-testid="missing-tax-close">Close</Button>
          {onTourNext && (
            <Button onClick={onTourNext}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    data-testid="missing-tax-tour-next">
              {tourNextLabel || "Next"} →
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function MissingEmailDialog({ state, onChangeRow, onSaveRow, onClose, tourStep, tourNextLabel, onTourNext }) {
  const rows = state?.rows || [];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="missing-email-dialog">
        <DialogHeader>
          <DialogTitle>Partners missing contact email</DialogTitle>
          <DialogDescription>
            Partners with commission activity but no <b>contact_email</b> — they&rsquo;re silently skipped by Nudge, per-row Email, and Reminders. Fill them here to unlock the flow.
          </DialogDescription>
        </DialogHeader>
        {state?.loading ? (
          <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" /></div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center">
            <div className="text-emerald-600 font-medium">All caught up 🎉</div>
            <div className="mt-1 text-xs text-slate-500">
              {state?.complete_count || 0} partner{(state?.complete_count === 1) ? "" : "s"} with commission activity all have a contact email.
            </div>
          </div>
        ) : (
          <div className="overflow-y-auto flex-1 -mx-6 px-6 space-y-2">
            <div className="text-xs text-slate-500">
              <b>{rows.length}</b> partner{rows.length === 1 ? "" : "s"} missing an email ·
              {" "}<b className="text-emerald-700">{state?.complete_count || 0}</b> already complete
            </div>
            <table className="w-full text-xs" data-testid="missing-email-table">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 pr-2">Partner</th>
                  <th className="text-left py-2 pr-2 w-56">Contact email</th>
                  <th className="text-left py-2 pr-2 w-40">Contact name</th>
                  <th className="text-left py-2 pr-2 w-32">Phone</th>
                  <th className="text-right py-2 pr-2 w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.org_id} data-testid={`missing-email-row-${r.org_id}`}>
                    <td className="py-2 pr-2 align-top">
                      <div className="font-medium text-slate-800">{r.name}</div>
                      <div className="text-[10px] text-slate-400">
                        {r.partner_type || "—"}
                        {r.commission_alias && r.commission_alias !== r.name && (
                          <> · alias {r.commission_alias}</>
                        )}
                      </div>
                    </td>
                    <td className="py-2 pr-2 align-top">
                      <Input type="email" value={r._contact_email}
                             onChange={(e) => onChangeRow(r.org_id, { _contact_email: e.target.value })}
                             placeholder="finance@partner.com"
                             className="h-7 text-xs"
                             data-testid={`missing-email-email-${r.org_id}`} />
                    </td>
                    <td className="py-2 pr-2 align-top">
                      <Input value={r._contact_name}
                             onChange={(e) => onChangeRow(r.org_id, { _contact_name: e.target.value })}
                             placeholder="Optional"
                             className="h-7 text-xs"
                             data-testid={`missing-email-name-${r.org_id}`} />
                    </td>
                    <td className="py-2 pr-2 align-top">
                      <Input value={r._contact_phone}
                             onChange={(e) => onChangeRow(r.org_id, { _contact_phone: e.target.value })}
                             placeholder="Optional"
                             className="h-7 text-xs"
                             data-testid={`missing-email-phone-${r.org_id}`} />
                    </td>
                    <td className="py-2 pr-2 text-right align-top">
                      <Button size="sm" onClick={() => onSaveRow(r)}
                              disabled={state?.savingOrgId === r.org_id || !r._contact_email}
                              className="h-7 bg-primary text-primary-foreground hover:bg-primary/90"
                              data-testid={`missing-email-save-${r.org_id}`}>
                        {state?.savingOrgId === r.org_id
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : "Save"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          {tourStep && (
            <div className="mr-auto flex items-center gap-2 text-xs text-slate-500" data-testid="tour-footer-missing-email">
              <span className="rounded bg-primary/10 px-2 py-1 font-semibold text-primary">Tour · Step {tourStep}</span>
              <span>Fill (or skip) rows, then continue</span>
            </div>
          )}
          <Button variant="outline" onClick={onClose} data-testid="missing-email-close">Close</Button>
          {onTourNext && (
            <Button onClick={onTourNext}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    data-testid="missing-email-tour-next">
              {tourNextLabel || "Next"} →
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SandboxLogDialog({ state, onReload, onClose }) {
  const items = state?.items || [];
  const f = state?.filters || { q: "", date_from: "", date_to: "" };
  const [q,        setQ]        = useState(f.q);
  const [dateFrom, setDateFrom] = useState(f.date_from);
  const [dateTo,   setDateTo]   = useState(f.date_to);
  const apply = () => onReload({ q: q.trim(), date_from: dateFrom, date_to: dateTo });
  const reset = () => { setQ(""); setDateFrom(""); setDateTo(""); onReload({}); };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="sandbox-log-dialog">
        <DialogHeader>
          <DialogTitle>Sandbox log</DialogTitle>
          <DialogDescription>
            The last {items.length} emails that <b>would have been sent</b> if sandbox mode were off. Nothing here was actually delivered.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_140px_auto] gap-2 pb-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && apply()}
                 placeholder="Search partner / recipient / kind…"
                 data-testid="sandbox-log-search" />
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                 data-testid="sandbox-log-date-from" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                 data-testid="sandbox-log-date-to" />
          <div className="flex items-center gap-1">
            <Button size="sm" onClick={apply}
                    className="h-9 bg-primary hover:bg-primary/90 text-primary-foreground"
                    data-testid="sandbox-log-apply">
              Apply
            </Button>
            {(state?.filtered || q || dateFrom || dateTo) && (
              <Button size="sm" variant="outline" onClick={reset}
                      className="h-9" data-testid="sandbox-log-reset">
                Reset
              </Button>
            )}
          </div>
        </div>
        {state?.loading ? (
          <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-400">
            {state?.filtered
              ? "Nothing matches the current filters."
              : "No sandbox activity yet — try clicking Nudge or Email on any partner."}
          </div>
        ) : (
          <div className="overflow-y-auto flex-1 -mx-6 px-6">
            <table className="w-full text-xs" data-testid="sandbox-log-table">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200 sticky top-0 bg-white">
                <tr>
                  <th className="text-left py-2 pr-2 w-32">When</th>
                  <th className="text-left py-2 pr-2 w-28">Kind</th>
                  <th className="text-left py-2 pr-2">Partner</th>
                  <th className="text-left py-2 pr-2 w-24">Period</th>
                  <th className="text-left py-2 pr-2">Would email</th>
                  <th className="text-right py-2 pr-2 w-24">Amount</th>
                  <th className="text-left py-2 pr-2 w-32">Triggered by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((i, idx) => (
                  <tr key={idx} data-testid={`sandbox-log-row-${idx}`}>
                    <td className="py-1.5 pr-2 tabular-nums text-slate-500">
                      {(i.sent_at || "").slice(0, 16).replace("T", " ")}
                    </td>
                    <td className="py-1.5 pr-2">
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">
                        {i.kind || "email"}
                      </span>
                    </td>
                    <td className="py-1.5 pr-2 text-slate-800">{i.partner_key}</td>
                    <td className="py-1.5 pr-2 text-slate-600">{i.period_label}</td>
                    <td className="py-1.5 pr-2 text-slate-600 truncate max-w-[240px]">
                      {(i.to || []).join(", ")}
                      {(i.cc || []).length > 0 && (
                        <span className="text-slate-400"> cc {i.cc.join(", ")}</span>
                      )}
                    </td>
                    <td className="py-1.5 pr-2 text-right tabular-nums text-slate-800">
                      ${Number(i.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="py-1.5 pr-2 text-slate-500 truncate max-w-[120px]">
                      {i.sent_by_name || i.sent_by || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="sandbox-log-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const downloadRemindersCsv = (r, unticked) => {
  const rows = [["Status", "Partner", "Email", "Net payable", "Accepted", "Reason"],
    ...(r.eligible || []).map((p) => [unticked.includes(p.partner_key) ? "Unticked" : "Eligible",
      p.name, p.email, p.net.toFixed(2), p.accepted ? "Yes" : "No", ""]),
    ...(r.skipped || []).map((p) => ["Skipped", p.name, "", p.net.toFixed(2), "", p.reason])];
  const blob = new Blob([rows.map((row) => row.map(csvCell).join(",")).join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `payment-reminders-${r.period_label}${r.dry_run ? "-preview" : ""}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const RecipientList = ({ title, items, testid, render, extra }) => (
  <div data-testid={testid}>
    <div className="mb-1 flex items-center text-[10px] font-semibold uppercase tracking-wider text-slate-500">
      {title} ({items.length}){extra}
    </div>
    {items.length === 0 ? <div className="text-slate-400">None</div> : (
      <ul className="max-h-48 divide-y divide-sky-100 overflow-y-auto rounded border border-sky-100 bg-white">
        {items.map((it, i) => <li key={it.partner_key || i} className="flex items-center gap-2 px-2 py-1">{render(it)}</li>)}
      </ul>
    )}
  </div>
);

function AddEmailInline({ item, onSaved }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      await api.patch(`/commissions/partners/orgs/${item.org_id}/contact`, { contact_email: email.trim() });
      toast.success(`Email saved for ${item.name}`);
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save email");
    } finally { setBusy(false); }
  };
  if (!item.org_id) return <span className="text-[10px] text-slate-400" title="No partner record is linked to this agent">No partner</span>;
  if (!open) return (
    <button type="button" onClick={() => setOpen(true)} data-testid={`reminders-add-email-${item.partner_key}`}
            className="rounded border border-sky-200 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 hover:bg-sky-50">
      Add email
    </button>
  );
  return (
    <span className="flex items-center gap-1">
      <Input autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@partner.com"
             onKeyDown={(e) => e.key === "Enter" && save()}
             className="h-6 w-36 px-1.5 text-[11px]" data-testid={`reminders-email-input-${item.partner_key}`} />
      <button type="button" onClick={save} disabled={busy} data-testid={`reminders-email-save-${item.partner_key}`}
              className="rounded bg-sky-600 px-1.5 py-0.5 text-[10px] font-medium text-white hover:bg-sky-700 disabled:opacity-50">
        {busy ? "…" : "Save"}
      </button>
      <button type="button" onClick={() => setOpen(false)} className="text-[10px] text-slate-400 hover:text-slate-600">Cancel</button>
    </span>
  );
}

function ReminderRecipients({ result, unticked, onToggle, onEmailAdded }) {
  if (!result.eligible && !result.skipped) return null;
  const editable = !!result.dry_run;
  const eligible = result.eligible || [];
  const ticked = eligible.filter((p) => !unticked.includes(p.partner_key)).length;
  return (
    <div className="mt-3 space-y-2">
      <div className="flex items-center justify-between">
        {editable
          ? <span className="text-sky-800" data-testid="reminders-ticked-count">{ticked} of {eligible.length} ticked — Send now emails only ticked partners</span>
          : <span />}
        <button type="button" onClick={() => downloadRemindersCsv(result, unticked)} data-testid="reminders-download-csv"
                className="inline-flex items-center gap-1 rounded border border-sky-200 bg-white px-2 py-0.5 text-[11px] font-medium text-sky-700 hover:bg-sky-50">
          <Download className="h-3 w-3" /> Download CSV
        </button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <RecipientList title="Eligible" items={eligible} testid="reminders-eligible-list"
          extra={editable && eligible.length > 0 && (
            <button type="button" className="ml-auto normal-case tracking-normal text-sky-700 hover:underline"
                    data-testid="reminders-toggle-all"
                    onClick={() => (ticked === eligible.length ? eligible : eligible.filter((p) => unticked.includes(p.partner_key)))
                      .forEach((p) => onToggle(p.partner_key))}>
              {ticked === eligible.length ? "Untick all" : "Tick all"}
            </button>
          )}
          render={(p) => (<>
            {editable && <Checkbox checked={!unticked.includes(p.partner_key)} onCheckedChange={() => onToggle(p.partner_key)}
                                   data-testid={`reminders-tick-${p.partner_key}`} />}
            <span className={`min-w-0 flex-1 ${unticked.includes(p.partner_key) ? "opacity-50" : ""}`} title={p.email}>
              <span className="block truncate font-medium text-slate-700">{p.name}</span>
              <span className="block truncate text-[10px] text-slate-400">{p.email}</span></span>
            {p.accepted && <span className="rounded bg-violet-100 px-1 text-[10px] text-violet-700">Accepted</span>}
            <span className="tabular-nums text-emerald-700">{money(p.net)}</span>
          </>)} />
        <RecipientList title="Skipped" items={result.skipped || []} testid="reminders-skipped-list" render={(p) => (<>
          <span className="min-w-0 flex-1" title={p.reason}>
            <span className="block truncate font-medium text-slate-700">{p.name}</span>
            <span className="block truncate text-[10px] text-amber-700">{p.reason}</span></span>
          {editable && p.reason === "No contact email" && <AddEmailInline item={p} onSaved={onEmailAdded} />}
          <span className="tabular-nums text-slate-500">{money(p.net)}</span>
        </>)} />
      </div>
    </div>
  );
}
